import React from 'react';
import { isNil } from 'lodash';
import { Col } from 'antd';
import { Link } from 'react-router-dom';

import { CompanyType } from '../types/CompanyType';
import { getContractorCompanyTypeLabel } from 'utils/companyUtils';
import { getFullName } from 'utils/personUtils';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { getLocation } from 'utils/locationUtils';
import { getContractorCompanyInfoFile } from 'redux/reducers/contractorCompanySlice';
import { downloadFile } from 'utils/fileUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { ContractorCompanyTypeEnum } from 'api/backofficeApiClient';

type Props = {
  company: CompanyType;
};
export default function CompanyGeneralInfo(props: Props) {
  const { company } = props;
  const {
    contractorCompanyType,
    companyKey,
    assignmentsCount,
    representativeContact,
    location,
    id: companyId,
    memberCompanyFirstName,
    memberCompanyLastName,
    memberKey,
    companyName,
  } = company;
  const isAdmin = useAppSelector(isAdminSelector);
  const dispatch = useAppDispatch();

  const primaryContactFullName = getFullName(
    representativeContact?.firstName,
    representativeContact?.lastName,
  );

  const memberCompany = getFullName(
    memberCompanyFirstName,
    memberCompanyLastName,
  );

  const businessAddress = getLocation(
    location?.address,
    location?.country,
    location?.state || location?.internationalStateName,
    location?.city || location?.internationalCityName,
    location?.zipCode,
  );

  const handleDownloadContractorCompanyFile = async () => {
    if (!companyId) return;

    const result = await dispatch(
      getContractorCompanyInfoFile({
        companyId,
        isAdminRole: isAdmin as boolean,
      }),
    );

    if (getContractorCompanyInfoFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  return (
    <>
      {!isNil(companyKey) && (
        <Col xs={24} md={12}>
          <div className="title">Contractor ID:</div>
          <div>{companyKey}</div>
        </Col>
      )}
      {!isNil(contractorCompanyType) && (
        <Col xs={24} md={12}>
          <div className="title">Company type:</div>
          <div>{getContractorCompanyTypeLabel(contractorCompanyType)}</div>
        </Col>
      )}
      {!isNil(assignmentsCount) && (
        <Col xs={24} md={12}>
          <div className="title">Number of assignments:</div>
          <div>{assignmentsCount}</div>
        </Col>
      )}
      {!!primaryContactFullName && (
        <Col xs={24} md={12}>
          <div className="title">Primary contact:</div>
          <div>{primaryContactFullName}</div>
        </Col>
      )}
      {!isNil(representativeContact?.email) && (
        <Col xs={24} md={12}>
          <div className="title">Email:</div>
          <div>{representativeContact?.email}</div>
        </Col>
      )}
      {!isNil(memberCompany) && (
        <Col xs={24} md={12}>
          <div className="title">Member company:</div>
          <div>
            {isAdmin ? (
              <Link to={`/member/${memberKey}`}>{memberCompany}</Link>
            ) : (
              <span>{memberCompany}</span>
            )}
          </div>
        </Col>
      )}
      {!isNil(representativeContact?.phoneNumber) && (
        <Col xs={24} md={12}>
          <div className="title">Phone number:</div>
          <div>{representativeContact?.phoneNumber}</div>
        </Col>
      )}
      {contractorCompanyType === ContractorCompanyTypeEnum.ParentSC &&
        !isNil(representativeContact?.taxId) && (
          <Col xs={24} md={12}>
            <div className="title">Tax ID:</div>
            <div>{representativeContact?.taxId}</div>
          </Col>
        )}
      {!!businessAddress && (
        <Col xs={24} md={12}>
          <div className="title">Business address:</div>
          <div>{businessAddress}</div>
        </Col>
      )}
      {contractorCompanyType === ContractorCompanyTypeEnum.ParentSC &&
        !isNil(representativeContact?.workerFileKey) && (
          <Col xs={24} md={12}>
            <div className="title">W9:</div>
            <div>
              <span
                className="link-imitation"
                onClick={handleDownloadContractorCompanyFile}
              >
                {`${companyName} - W9`}
              </span>
            </div>
          </Col>
        )}
    </>
  );
}
